import type { LeaderboardMetric } from "@expert/app-config/types";

import { SalesProvider } from "@expert/contentful";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { Divider, Flex, ScrollArea, Skeleton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Reorder } from "framer-motion";
import { useEffect } from "react";

import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useProductOfferStore } from "@/sales-tools/home-product";
import { EnrollmentStatus } from "@/sales-tools/home-product/types";
import { useSessionId } from "@/sdk";
import { userCache } from "@/shared-utils";

import { useLeaderboard } from "../hooks/useLeaderboard";
import classes from "./Leaderboard.module.css";

const splitName = (name: string) => {
    const names = name.split(" ");
    if (!names[1]) return names[0];
    return `${names[0]} ${names[1].charAt(0)}.`;
};

interface LeaderboardProps {
    isVisible: boolean;
    metric: LeaderboardMetric;
    sku?: string;
}

interface SalesEventPayload {
    status: EnrollmentStatus;
    offerId: string;
    salesProvider: SalesProvider;
}

export function Leaderboard({ isVisible, metric, sku }: LeaderboardProps) {
    const queryClient = useQueryClient();
    const { leaderboards } = usePartnerConfig();
    const salesLeaderboard = leaderboards.find((leaderboard) => leaderboard.metric === "sales");
    const sessionId = useSessionId();
    const { data, isLoading } = useLeaderboard(metric, sku);
    useEffect(() => {
        const callsSubscription = expertWorkspaceWebSocketEventBus.on("ws_calls-event", (payload) => {
            void queryClient.setQueryData(["calls-leaderboard"], payload);
        });

        const enrollmentSubscription = expertWorkspaceWebSocketEventBus.on(`ws_enrollment-event`, (payload) => {
            const enrollmentEventPayload = payload as SalesEventPayload;
            const products = useProductOfferStore.getState().getProducts(sessionId);
            const enrolledOffer = products.find((p) => p.id === enrollmentEventPayload.offerId);

            if (
                salesLeaderboard &&
                enrolledOffer?.productSku === salesLeaderboard.sku &&
                enrollmentEventPayload.status === "SUCCESS"
            ) {
                void queryClient.refetchQueries({
                    queryKey: ["sales-leaderboard"],
                });
            }
        });

        return () => {
            callsSubscription();
            enrollmentSubscription();
        };
    }, [queryClient, metric, sessionId, salesLeaderboard]);

    if (!isVisible) return null;
    return (
        <ScrollArea type="never">
            <Flex direction="column">
                <Divider />
                {isLoading ? (
                    <Flex direction="column" justify="space-between" w="188px">
                        {Array.from({ length: 12 }, () => (
                            <Skeleton key={Math.random().toString(33)} mb="4xs" h="xl" mx="md" />
                        ))}
                    </Flex>
                ) : data && data.length > 0 ? (
                    <Reorder.Group as="div" values={data} onReorder={() => undefined}>
                        {data.map((info) => (
                            <Reorder.Item as="div" dragListener={false} key={info.employeeId} value={info}>
                                <Flex
                                    key={info.employeeId}
                                    justify="space-between"
                                    fz="xs"
                                    align="center"
                                    h={36}
                                    py="3xs"
                                    px="lg"
                                    className={userCache.employeeId === info.employeeId ? classes.purple : ""}
                                >
                                    <Flex>
                                        {info.order + 1}.{" "}
                                        {info.employeeId === userCache.employeeId
                                            ? "You"
                                            : splitName(info.employeeName)}
                                    </Flex>
                                    <Flex pl="xl">{info.score}</Flex>
                                </Flex>
                                <Divider />
                            </Reorder.Item>
                        ))}
                    </Reorder.Group>
                ) : (
                    <Flex
                        direction="column"
                        mt="3rem"
                        align="center"
                        justify="center"
                        p="md"
                        fw={400}
                        c="dark.2"
                        fz="xs"
                    >
                        Leaderboard data unavailable.
                    </Flex>
                )}
            </Flex>
        </ScrollArea>
    );
}
